import { CSSProperties } from 'react';
import { POV_SEAT_EXTRA_DROP_VH } from 'constants/gameConstants';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { TableZ } from 'styles/zIndex';

import { seatEdgeSafeLeft } from './seatEdgeInsets';

/**
 * Seat styles are split into a positioned **wrapper** and an inner **content**
 * box. The wrapper owns the table-relative position and the responsive
 * `--seat-scale` (set on the table root); the content box is the flex layout and
 * the element GSAP animates (fade-in / win bounce). Keeping the responsive scale
 * off the GSAP-animated element prevents GSAP from overwriting it — the two
 * scales compose instead of fighting.
 */

/**
 * The POV seat's vertical anchor — its ring position plus the extra push that
 * clears the felt's bottom rail.
 *
 * Exported so the POV bet pill can be placed RELATIVE to it. The pill has to
 * clear the dealer marker that hangs off the top of this seat, and the only way
 * that gap can be a fixed size is if both are written against the same anchor
 * in the same units — see SELF_BET_ABOVE_SEAT_PX.
 */
export const povSeatTop = (topPct: number): string =>
  `calc(${topPct}% + ${POV_SEAT_EXTRA_DROP_VH}vh)`;

/**
 * How far a BOTTOM-row opponent seat's box is lifted over its ring position, as
 * a fraction of its own (unscaled) height. Exported for the bet placement
 * (hooks/useEllipticalPositions), which has to know where this seat's card top —
 * and so its action bubble — actually paints.
 */
export const PORTRAIT_BOTTOM_SEAT_ANCHOR_Y = 0.2;

export const getPortraitModeStyles = (position: SeatPosition, disableTransition = false) => {
  // During the POV seat-assignment rotation, positions are stepped every frame
  // along the ellipse arc, so the smoothing transition is turned off to avoid
  // smearing/lag; it resumes for the usual single position changes.
  const transition = disableTransition ? 'none' : 'left 0.5s ease, top 0.5s ease';
  const getMyPlayerWrapperStyle = (): CSSProperties => ({
    position: 'absolute',
    left: seatEdgeSafeLeft(position.leftPct, true),
    // EN-10520: the whole table now sits TABLE_DOWN_SHIFT_PCT lower (baked into
    // position.topPct), and the POV seat is the one element with no room below
    // it — the action row is right there. So its own extra push shrinks from
    // 5vh to 2.5vh: it still clears the felt's bottom rail, but the net move is
    // a few px rather than the full table drop, and nothing under it clips.
    //
    // The push is NAMED because the POV bet pill (Table/Bet) hangs off this
    // exact anchor to stay clear of the seat's dealer marker. A literal here
    // would let the two expressions drift apart silently.
    top: povSeatTop(position.topPct),
    transform: 'translate(-50%, -50%) scale(var(--seat-scale, 1))',
    transformOrigin: 'center center',
    transition,
    // Table z-order model (Jason 8/13, EN-10420; banded in styles/zIndex.ts by
    // EN-10520): the POV player's hand outranks everything ON the felt — bet
    // chips and the dealer button are table elements, the POV seat is a seat
    // element and wins ties against other seats. Static, all situations.
    zIndex: TableZ.seatPov,
    overflow: 'visible'
  });

  const getMyPlayerContentStyle = (): CSSProperties => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    overflow: 'visible'
  });

  const getOpponentWrapperStyle = (): CSSProperties => {
    const pYAnchor = position.isTop
      ? '-30%'
      : position.isBottom
      ? `-${PORTRAIT_BOTTOM_SEAT_ANCHOR_Y * 100}%`
      : '-50%';
    return {
      position: 'absolute',
      left: seatEdgeSafeLeft(position.leftPct, true),
      top: `${position.topPct}%`,
      transform: `translate(-50%, ${pYAnchor}) scale(var(--seat-scale, 1))`,
      transformOrigin: 'center center',
      transition,
      // Seat band — above every table element (bet chips, board, dealer
      // button). This used to be 3, i.e. BELOW the floating dealer button's 5,
      // which is how the button ended up painted over revealed hole cards.
      zIndex: TableZ.seat,
      overflow: 'visible'
    };
  };

  const getOpponentContentStyle = (): CSSProperties => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    overflow: 'visible'
  });

  return {
    getMyPlayerWrapperStyle,
    getMyPlayerContentStyle,
    getOpponentWrapperStyle,
    getOpponentContentStyle
  };
};
